import React, { useEffect, useState } from 'react';
import { supplyView } from '../utils/supply';

// ── "Time until empty" (trial) ────────────────────────────────────────────
//
// A one-line strip at the bottom of a tank card, and the popup it opens. Shown
// only on monitors the admin switched on (features.supplyTrial). The rate
// comes from the cloud (devices/{id}.supply.tankN); utils/supply.js turns it
// and the live gallons into words. On a monitor without SoKo Plus it is a
// quiet locked line; tapping it opens the SoKo Plus prompt (onLocked).

function LockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="4.5" y="10.5" width="15" height="10" rx="2" /><path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
    </svg>
  );
}

function SupplySheet({ view, gallons, tankLabel, onClose }) {
  const [howOpen, setHowOpen] = useState(false);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="supply-sheet__backdrop" onClick={onClose} role="presentation">
      <div className="supply-sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Time until empty">
        <div className="supply-sheet__x-row">
          <button type="button" className="supply-sheet__x" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className={`supply-sheet__figs ${view.nextAlert ? '' : 'supply-sheet__figs--one'}`}>
          <div className="supply-sheet__fig">
            <div className="supply-sheet__figlabel">Time until empty</div>
            <div className={`supply-sheet__big ${view.soon ? 'is-soon' : ''}`}>{view.big}</div>
          </div>
          {view.nextAlert && (
            <div className="supply-sheet__fig">
              <div className="supply-sheet__figlabel">Next low-water alert ({view.nextAlert.pct}%)</div>
              <div className="supply-sheet__big">{view.nextAlert.label}</div>
            </div>
          )}
        </div>
        <div className="supply-sheet__sub">
          {tankLabel ? `${tankLabel} · ` : ''}{Math.round(gallons).toLocaleString()} gallons in tank
        </div>
        <div className="supply-sheet__basis">{view.sheetLine}</div>
        <div className="supply-sheet__howbox">
          <button type="button" className="supply-sheet__howhead" aria-expanded={howOpen} onClick={() => setHowOpen((v) => !v)}>
            <span>How it&apos;s calculated</span>
            <svg className={`collapse-chev ${howOpen ? 'is-open' : ''}`} width="18" height="18" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
          </button>
          {howOpen && (
            <>
              <p>We estimate how long your water will last based on your typical use over the past two weeks. One unusually high or low day won&apos;t change the estimate much, but if you&apos;ve been using more water lately, we use that higher rate instead.</p>
              <p>Rain and deliveries aren&apos;t counted as water use, and future rain isn&apos;t predicted.</p>
              <p>The estimate counts down to your tank&apos;s 0% level, the empty depth you set during setup. Actual usable water may vary.</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SupplyStrip({ est, gallons, pct, alerts, offline, locked, onLocked, tankLabel }) {
  const [open, setOpen] = useState(false);

  if (locked) {
    return (
      <button type="button" className="supply2 supply2--locked" onClick={onLocked}>
        <span className="supply2__row">
          <span className="supply2__label">Time until empty</span>
          <span className="supply2__lock"><LockIcon /> SoKo Plus</span>
        </span>
      </button>
    );
  }

  const view = supplyView({ est, gallons, pct, alerts, offline });
  if (!view) return null;
  const muted = view.state !== 'estimate';
  const chev = <span className="supply2__chev" aria-hidden="true">›</span>;

  return (
    <>
      <button type="button"
        className={`supply2 ${view.soon ? 'supply2--soon' : ''} ${muted ? 'supply2--muted' : ''}`}
        onClick={() => { if (!muted) setOpen(true); }}
        aria-disabled={muted}>
        <span className="supply2__row">
          <span className="supply2__label">Time until empty</span>
          {muted
            ? <span className="supply2__status">{view.big}</span>
            : <span className="supply2__value">{view.big}{chev}</span>}
        </span>
      </button>
      {open && <SupplySheet view={view} gallons={gallons} tankLabel={tankLabel} onClose={() => setOpen(false)} />}
    </>
  );
}
